// The eight sided die as a solid: six corners at distance one from the center,
// eight triangle faces. Face `i` shows the number `i + 1`. Opposite faces sum
// to nine, as on a real die.

import type { Side } from '../engine/types';
import { cross, type Mat3, normalize, scale, subtract, type Vec3 } from './vector';

const FACE_COUNT = 8;
const CORNERS_PER_FACE = 3;

export type DieFace = {
  corners: readonly [Vec3, Vec3, Vec3];
  /** Points out of the die. */
  normal: Vec3;
  center: Vec3;
  /** In the plane of the face, from the center toward the top of the number. */
  up: Vec3;
  /** In the plane of the face, the reading direction of the number. */
  right: Vec3;
};

/** What a face reads: text, a count of coins of energy, or the mark of a side for the coin flip of a room. */
export type DieFaceLabel = string | { coins: number } | { side: Side };

/** Each bit of the index picks the sign of one axis, so face `7 - i` is opposite face `i`. */
const faceAt = (index: number): DieFace => {
  const signX = index & 1 ? -1 : 1;
  const signY = index & 2 ? -1 : 1;
  const signZ = index & 4 ? -1 : 1;
  const yCorner: Vec3 = [0, signY, 0];
  const center = scale([signX, signY, signZ], 1 / CORNERS_PER_FACE);
  const normal = normalize([signX, signY, signZ]);
  const up = normalize(subtract(yCorner, center));
  return {
    corners: [[signX, 0, 0], yCorner, [0, 0, signZ]],
    normal,
    center,
    up,
    right: cross(up, normal),
  };
};

export const DIE_FACES: readonly DieFace[] = Array.from({ length: FACE_COUNT }, (_, index) =>
  faceAt(index),
);

/** The rotation that turns `face` to the viewer with its number upright. */
export const landingOrientation = (face: number): Mat3 => {
  const landed = DIE_FACES[face];
  if (!landed) throw new Error(`the die has no face ${face}`);
  return [landed.right, landed.up, landed.normal];
};
